/**
 * this is Order Confirmation items : Your Items
 *
 * @module views/components/OrderConfirmationItems
 * @memberof -Common
 */
import './OrderConfirmationItems.scss';

import React from 'react';

import PropTypes from 'prop-types';

import { hasItems } from '@ulta/core/utils/array/array';
import { spreadProps } from '@ulta/core/utils/dom/dom';

import SummaryProductCard from '@ulta/modules/SummaryProductCard/SummaryProductCard';
import Text from '@ulta/modules/Text/Text';

import { descendingHtmlTag } from '@ulta/utils/accessibility/accessibility';


/**
 * Represents a OrderConfirmationItems component
 *
 * @method
 * @param {OrderConfirmationItemsProps} props - React properties passed from composition
 * @returns OrderConfirmationItems
 */

export const OrderConfirmationItems = function( props ){
  const {
    giftItems,
    giftItemsTitle,
    giftItemsSubtitle,
    componentKey,
    items,
    itemsTitle,
    itemsSubtitle,
    listPriceAccessibilityLabel,
    salePriceAccessibilityLabel
  } = props;

  const hasProductItems = hasItems( items );
  const hasGifts = hasItems( giftItems );

  if( !hasProductItems && !hasGifts ){
    return null;
  }

  return (
    <div className='OrderConfirmationItems' >
      { hasProductItems && (
        <section className='OrderConfirmationItems__itemsPurchasedSection'>
          { itemsTitle?.text && (
            <div className='OrderConfirmationItems__itemsTitle'>
              <Text htmlTag={ itemsTitle?.htmlTag }
                textStyle='title-5'
                color='black'
              >
                { itemsTitle.text }
              </Text>
            </div>
          ) }
          { itemsSubtitle && (
            <div className='OrderConfirmationItems__itemsSubtitle'>
              <Text
                color='neutral-600'
                htmlTag='p'
                textStyle='body-2'
              >
                { itemsSubtitle }
              </Text>
            </div>
          ) }
          <div className='OrderConfirmationItems__summaryProductCardItems'>
            <ul className='OrderConfirmationItems__cardItemsList'>
              { items.map( ( product, productIndex ) => (
                <li key={ `${componentKey}-summaryItems-list-${productIndex}` } >
                  <SummaryProductCard
                    ariaLabel='product'
                    key={ `${componentKey}-summary-items-${productIndex}` }
                    textColor='neutral-600'
                    productNameTag={ descendingHtmlTag( itemsTitle?.htmlTag ) }
                    { ...spreadProps( { ...product } ) }
                  />
                </li>
              ) ) }
            </ul>
          </div>
        </section>
      ) }
      { hasGifts && (
        <section className='OrderConfirmationItems__giftWithPurchaseSection'>
          { giftItemsTitle?.text && (
            <div className='OrderConfirmationItems__giftItemsTitle'>
              <Text htmlTag={ giftItemsTitle?.htmlTag }
                textStyle='title-5'
                color='black'
              >
                { giftItemsTitle.text }
              </Text>
            </div>
          ) }
          { giftItemsSubtitle && (
            <div className='OrderConfirmationItems__giftItemsSubtitle'>
              <Text
                color='neutral-600'
                htmlTag='p'
                textStyle='body-2'
              >
                { giftItemsSubtitle }
              </Text>
            </div>
          ) }
          <div className='OrderConfirmationItems__giftWithPurchaseItems'>
            <ul className='OrderConfirmationItems__cardItemsList'>
              { giftItems.map( ( giftItem, giftIndex ) => (
                <li key={ `${componentKey}-giftItem-list-${giftIndex}` } >
                  <SummaryProductCard
                    ariaLabel='gift'
                    key={ `${componentKey}-gift-items-${giftIndex}` }
                    { ...spreadProps( { ...giftItem } ) }
                    productNameTag={ descendingHtmlTag( giftItemsTitle?.htmlTag ) }
                    listPriceAccessibilityLabel={ listPriceAccessibilityLabel }
                    salePriceAccessibilityLabel={ salePriceAccessibilityLabel }
                  />
                </li>
              ) ) }
            </ul>
          </div>
        </section>
      ) }
    </div>
  );
};

/**
 * Property type definitions
 * @typedef OrderConfirmationItemsProps
 * @type {object}
 * @property {string} componentKey - Sets the componentKey
 * @property {object} giftItems - Sets gift items object
 * @property {object} giftItemsTitle - Gift with purchase title and htmlTag
 * @property {string} giftItemsSubtitle - Gift with purchase subtitle
 * @property {object} items - Sets product items object
 * @property {object} itemsTitle - set items title and htmlTag
 * @property {string} itemsSubtitle - set items subtitle
 * @property {string} listPriceAccessibilityLabel - accessiblity label for products listing price
 * @property {string} salePriceAccessibilityLabel - accessiblity label for products final price
 */

export const propTypes = {
  componentKey: PropTypes.string,
  giftItems: PropTypes.arrayOf(
    PropTypes.shape( {
      brandName: PropTypes.string,
      productName: PropTypes.string,
      variantTypeLabel: PropTypes.string,
      variantTypeDesc: PropTypes.string,
      quantityLabel: PropTypes.oneOfType( [PropTypes.string, PropTypes.number] ),
      image: PropTypes.object,
      finalPrice: PropTypes.string
    } )
  ),
  giftItemsTitle: PropTypes.object,
  giftItemsSubtitle: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape( {
      brandName: PropTypes.string,
      productName: PropTypes.string,
      variantTypeLabel: PropTypes.string,
      variantTypeDesc: PropTypes.string,
      quantityLabel: PropTypes.oneOfType( [PropTypes.string, PropTypes.number] ),
      image: PropTypes.object,
      finalPrice: PropTypes.string,
      deals: PropTypes.array,
      unitPriceBreakup: PropTypes.string
    } )
  ),
  itemsTitle: PropTypes.object,
  itemsSubtitle: PropTypes.string,
  listPriceAccessibilityLabel: PropTypes.string,
  salePriceAccessibilityLabel: PropTypes.string
};

OrderConfirmationItems.propTypes = propTypes;

export default OrderConfirmationItems;
